import { createFileRoute, Link } from "@tanstack/react-router";
import { PageShell, Card } from "@/components/page-shell";
import { EndNote, H2 } from "@/components/guide-page";
// Value import used only by the component below, so the route splitter keeps it out
// of the eager chunk; the loader re-imports it dynamically for the JSON-LD.
import { speciesForHub, type Species } from "@/lib/species";
import { breadcrumbJsonLd } from "@/lib/jsonld";
import { absUrl, ogImageUrl } from "@/lib/site";
import { pageTitle } from "@/lib/seo";

const PATH = "/ticks";
const OG = ogImageUrl({
  title: "Tick species field guide",
  subtitle: "Identify the ticks that bite people & pets",
  kind: "guide",
});

export const Route = createFileRoute("/ticks/")({
  // The lookup is a dynamic import: routeTree.gen.ts pulls every route's loader and
  // head into the client's entry chunk, so a top-level import here would put the
  // whole species field guide on the home page's critical path.
  loader: async (): Promise<{ parts: Array<{ name: string; url: string }> }> => {
    const { speciesForHub: hub } = await import("@/lib/species");
    return {
      parts: hub().map((s) => ({ name: s.commonName, url: absUrl(`/ticks/${s.slug}`) })),
    };
  },
  head: ({ loaderData }) => {
    const parts = loaderData?.parts ?? [];
    return {
      meta: [
        { title: pageTitle("Tick Species Field Guide: Identify the Ticks That Bite") },
        {
          name: "description",
          content:
            "Photos, range and diseases for 8 common ticks that bite people and pets in the US, from the Lone Star and deer tick to the Asian longhorned tick.",
        },
        { property: "og:title", content: "Tick species field guide" },
        {
          property: "og:description",
          content: "Identify the ticks that bite people and pets, and the diseases each carries.",
        },
        { property: "og:url", content: absUrl(PATH) },
        { property: "og:image", content: OG },
        { name: "twitter:title", content: "Tick species field guide" },
        { name: "twitter:image", content: OG },
      ],
      links: [{ rel: "canonical", href: absUrl(PATH) }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            name: "Tick species field guide",
            url: absUrl(PATH),
            hasPart: parts.map((p) => ({ "@type": "Article", name: p.name, url: p.url })),
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify(
            breadcrumbJsonLd([
              { name: "Home", path: "/" },
              { name: "Tick species", path: PATH },
            ]),
          ),
        },
      ],
    };
  },
  component: SpeciesHub,
});

/** The meta description, without the boilerplate the page end already carries. */
function dek(s: Species): string {
  return s.metaDescription.replace(/\s*Educational, not medical advice\.?$/, "");
}

function SpeciesHub() {
  const species = speciesForHub();
  return (
    <PageShell
      crumbs={[
        { name: "Home", path: "/" },
        { name: "Tick species", path: PATH },
      ]}
      title="The ticks that bite people and pets"
      intro="Not every tick is a Lone Star tick, and different ticks carry different diseases. These are the common U.S. ticks: how to recognize each one by its markings and size, where it lives, and the illnesses it can transmit."
    >
      <Card>
        <ul className="divide-y divide-border">
          {species.map((s) => (
            <li
              key={s.slug}
              className="group relative grid gap-x-10 gap-y-2 py-6 first:pt-0 md:grid-cols-[17rem_1fr]"
            >
              <div>
                <h2 className="font-display text-2xl font-medium leading-tight text-foreground transition-colors group-hover:text-primary">
                  <Link
                    to="/ticks/$species"
                    params={{ species: s.slug }}
                    className="after:absolute after:inset-0 after:content-['']"
                  >
                    {s.commonName}
                  </Link>
                </h2>
                <p className="mt-1 text-sm italic text-muted-foreground">{s.scientificName}</p>
                {s.aliases.length > 0 && (
                  <p className="mt-1 text-xs text-muted-foreground">
                    Also called {s.aliases.join(", ")}
                  </p>
                )}
              </div>
              <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
                {s.image && (
                  <img
                    src={s.image.src}
                    alt=""
                    width={s.image.width ?? 575}
                    height={s.image.height ?? 546}
                    loading="lazy"
                    decoding="async"
                    className="w-28 shrink-0 rounded-sm border border-border sm:order-2 sm:w-32"
                  />
                )}
                <p className="measure text-[0.9375rem] leading-relaxed text-muted-foreground">
                  {dek(s)}
                </p>
              </div>
            </li>
          ))}
        </ul>
      </Card>

      <Card>
        <h2 className={H2}>Not sure what you found?</h2>
        <p className="measure mt-2 text-[0.9375rem] leading-relaxed text-muted-foreground">
          Answer a few questions about what you can see and narrow it down, or compare the common
          species side by side.
        </p>
        <div className="mt-5 flex flex-wrap items-center gap-x-6 gap-y-3">
          <Link
            to="/tick-identification"
            className="inline-flex rounded-sm bg-primary px-5 py-3 text-sm font-semibold text-primary-foreground hover:bg-primary-dark"
          >
            Identify a tick
          </Link>
          <Link to="/diseases" className="link text-sm font-semibold">
            Tick-borne diseases
          </Link>
          <Link to="/tick-bite-tool" className="link text-sm font-semibold">
            What to do after a bite
          </Link>
        </div>
      </Card>

      <EndNote>
        Educational only, and not a substitute for professional identification. If you were bitten,
        use the{" "}
        <Link to="/tick-bite-tool" className="link">
          tick bite tool
        </Link>{" "}
        and contact a licensed clinician if symptoms develop.
      </EndNote>
    </PageShell>
  );
}
